import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { crmQueryKeys } from '@/lib/crm/query/queries';
import type { CrmObjectListSentinelPage } from '@/lib/crm/store';
import {
  EMPTY_HIDDEN_MEETINGS,
  type HiddenMeetings,
  hiddenMeetingsQueryKey,
} from '@/lib/dashboard/hiddenMeetings';
import { todayMeetingsQueryOptions } from '@/lib/dashboard/queries';
import { apiPost } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import type { EntityIdFilter, FilterGroup } from '@/types/crm';
import { isAssociationFilter, isEntityIdFilter } from '@/types/crm';

export interface UseDismissMeetingResult {
  /** Hides a meeting from the current user's dashboard via the self-only
   * quickfetch dismiss endpoint. A call for an entity that already has a
   * dismiss in flight is a no-op. */
  dismissMeeting: (meetingEntityId: string) => Promise<void>;
  /** Whether a dismiss request for this specific entity is currently in flight. */
  isDismissing: (meetingEntityId: string) => boolean;
}

interface DismissResponse {
  dismissed: boolean;
  entity_id: string;
  hidden_meetings?: {
    series_ids: string[];
    meeting_entity_ids: string[];
  };
}

/**
 * Finds the `filter_groups` embedded in a cached ahoy_meeting list query's
 * key. crmObjectListSentinelQuery builds this key via
 * `crmQueryKeys.objectsListUnified`, whose params object nests the filters
 * under a `filters` key (`{ filters: { filter_groups, ... }, ... }`) rather
 * than exposing `filter_groups` directly on the key part — see
 * CrmObjectsListKeyParams in lib/crm/query/queries.ts. Returns null when the
 * key carries no such params object (e.g. a bare `crmQueryKeys.objects(...)`
 * prefix key), in which case there is nothing to derive a new key from.
 */
function extractFilterGroups(
  queryKey: readonly unknown[]
): FilterGroup[] | null {
  for (const part of queryKey) {
    if (!part || typeof part !== 'object') {
      continue;
    }
    const { filters } = part as { filters?: { filter_groups?: unknown } };
    if (filters && Array.isArray(filters.filter_groups)) {
      return filters.filter_groups as FilterGroup[];
    }
  }
  return null;
}

/**
 * Returns filterGroups with `meetingEntityId` added to (or newly inserted
 * into) each group's entity_id `notin` filter, at the same relative position
 * buildTodayMeetingsFilters itself would place it — immediately after the
 * date/status/series filters and before the association filter(s). Building
 * the new key this way (patching the OLD, already-correct filter_groups)
 * guarantees it hashes identically to what the live hook will compute once
 * the hidden-meetings cache update below propagates.
 */
function withHiddenEntityId(
  filterGroups: FilterGroup[],
  meetingEntityId: string
): FilterGroup[] {
  return filterGroups.map(group => {
    const { filters } = group;
    const assocIndex = filters.findIndex(isAssociationFilter);
    const baseEndIndex = assocIndex === -1 ? filters.length : assocIndex;
    const existingIndex = filters.findIndex(isEntityIdFilter);

    if (existingIndex !== -1 && existingIndex < baseEndIndex) {
      const existing = filters[existingIndex] as EntityIdFilter;
      if (existing.value.includes(meetingEntityId)) {
        return group;
      }
      const updated: EntityIdFilter = {
        subject: 'entity_id',
        operator: 'notin',
        value: [...existing.value, meetingEntityId].sort(),
      };
      const newFilters = [...filters];
      newFilters[existingIndex] = updated;
      return { filters: newFilters };
    }

    const newEntityIdFilter: EntityIdFilter = {
      subject: 'entity_id',
      operator: 'notin',
      value: [meetingEntityId],
    };
    return {
      filters: [
        ...filters.slice(0, baseEndIndex),
        newEntityIdFilter,
        ...filters.slice(baseEndIndex),
      ],
    };
  });
}

/**
 * Returns `{ dismissMeeting, isDismissing }` for hiding a meeting from the
 * current user's dashboard. `dismissMeeting` optimistically:
 *   1. Removes the meeting from every cached dashboard meetings-list query.
 *   2. Adds the meeting's id to the hidden-meetings cache (lib/dashboard/hiddenMeetings.ts) —
 *      this changes the filter_groups the live query will build next, and so
 *      its React-Query key. To avoid a cold refetch under that new key, it
 *      also seeds each new key from the OLD entry's data (minus the
 *      dismissed meeting) — see withHiddenEntityId/extractFilterGroups above.
 * On failure it rolls back both: it restores only this specific entity into
 * the *current* meetings-list cache (not a stale pre-call snapshot, so a
 * concurrent dismiss of a different meeting that already succeeded isn't
 * resurrected), and removes only this meeting's id from the *current*
 * hidden-meetings cache for the same reason.
 */
export function useDismissMeeting(orgId: number): UseDismissMeetingResult {
  const queryClient = useQueryClient();
  const inFlightRef = useRef<Set<string>>(new Set());
  // Ref mutations alone don't trigger a re-render, but callers (e.g. the
  // Dismiss button's `disabled` prop) need isDismissing() to reflect the
  // in-flight state immediately — this state bump forces that re-render.
  const [, forceRender] = useState(0);

  const isDismissing = useCallback(
    (meetingEntityId: string) => inFlightRef.current.has(meetingEntityId),
    []
  );

  const dismissMeeting = useCallback(
    async (meetingEntityId: string) => {
      if (!orgId) {
        return;
      }

      if (inFlightRef.current.has(meetingEntityId)) {
        logger.debug('Dismiss meeting: suppressed duplicate in-flight call', {
          meetingEntityId,
          orgId,
        });
        return;
      }
      inFlightRef.current.add(meetingEntityId);
      forceRender(n => n + 1);

      const key = crmQueryKeys.objects(orgId, 'ahoy_meeting');
      await queryClient.cancelQueries({ queryKey: key });

      // Track which (old) query keys had this id removed — not a body
      // snapshot, since the object itself never leaves the CRM store, only
      // its id leaves the list's sentinel `ids` array. A full-list snapshot
      // would resurrect other meetings that were legitimately dismissed by a
      // different in-flight call once this call's rollback restores it.
      const removedFromQueryKeys = new Set<string>();
      // New meetings-list keys seeded below (the hidden set becoming part of
      // filter_groups means dismissing changes the key) — tracked so a failed
      // dismiss can remove them again on rollback.
      const seededNewKeys: (readonly unknown[])[] = [];
      // Old meetings-list keys that were updated (meeting removed) — tracked
      // so finally can invalidate only those, not the newly seeded keys.
      const updatedOldQueryKeys: (readonly unknown[])[] = [];

      queryClient
        .getQueriesData<CrmObjectListSentinelPage>({ queryKey: key })
        .forEach(([queryKey, data]) => {
          if (!data || !Array.isArray(data.ids)) {
            return;
          }
          if (!data.ids.includes(meetingEntityId)) {
            return;
          }
          removedFromQueryKeys.add(JSON.stringify(queryKey));
          const remainingIds = data.ids.filter(id => id !== meetingEntityId);

          // Seed the NEW key (old filter_groups patched to hide this meeting)
          // from this OLD entry's data, so the live query finds a warm cache
          // entry once effectiveFilterGroups recomputes with the updated
          // hidden set — instead of cold-fetching (a visible flash).
          const oldFilterGroups = extractFilterGroups(queryKey);
          if (oldFilterGroups) {
            const newFilterGroups = withHiddenEntityId(
              oldFilterGroups,
              meetingEntityId
            );
            const newQueryKey = todayMeetingsQueryOptions(
              orgId,
              newFilterGroups
            ).queryKey;
            queryClient.setQueryData<CrmObjectListSentinelPage>(newQueryKey, {
              ...data,
              ids: remainingIds,
            });
            seededNewKeys.push(newQueryKey);
          }

          queryClient.setQueryData<CrmObjectListSentinelPage>(queryKey, {
            ...data,
            ids: remainingIds,
          });
          updatedOldQueryKeys.push(queryKey);
        });

      // Optimistically add this meeting to the hidden-meetings cache — a
      // functional update so a concurrent dismiss of a different meeting
      // (already applied or still in flight) is never overwritten.
      queryClient.setQueryData<HiddenMeetings>(
        hiddenMeetingsQueryKey(orgId),
        old => {
          const base = old ?? EMPTY_HIDDEN_MEETINGS;
          if (base.meetingEntityIds.includes(meetingEntityId)) {
            return base;
          }
          return {
            seriesIds: base.seriesIds,
            meetingEntityIds: [
              ...base.meetingEntityIds,
              meetingEntityId,
            ].sort(),
          };
        }
      );

      try {
        const response = await apiPost<DismissResponse>(
          `/api/orgs/${orgId}/quickfetch/dismiss/`,
          { entity_id: meetingEntityId }
        );
        if (response.hidden_meetings) {
          queryClient.setQueryData<HiddenMeetings>(
            hiddenMeetingsQueryKey(orgId),
            {
              seriesIds: response.hidden_meetings.series_ids,
              meetingEntityIds: response.hidden_meetings.meeting_entity_ids,
            }
          );
        }
      } catch (error) {
        // Remove only this meeting's id from the CURRENT hidden-meetings
        // cache, not a stale pre-call snapshot — a concurrent dismiss of a
        // different meeting that already succeeded must not be undone.
        queryClient.setQueryData<HiddenMeetings>(
          hiddenMeetingsQueryKey(orgId),
          old => {
            const base = old ?? EMPTY_HIDDEN_MEETINGS;
            return {
              seriesIds: base.seriesIds,
              meetingEntityIds: base.meetingEntityIds.filter(
                id => id !== meetingEntityId
              ),
            };
          }
        );
        // The speculative new keys hid this meeting under a hidden set that
        // no longer applies now that the dismiss failed — drop them.
        seededNewKeys.forEach(newQueryKey => {
          queryClient.removeQueries({ queryKey: newQueryKey, exact: true });
        });
        // Re-insert this id into the cache as it stands right now, not the
        // stale pre-call snapshot — other dismiss calls may have completed in
        // the interim and their removals must survive. The object body itself
        // was never removed from the CRM store, only its id from this list.
        queryClient
          .getQueriesData<CrmObjectListSentinelPage>({ queryKey: key })
          .forEach(([queryKey, data]) => {
            const wasRemoved = removedFromQueryKeys.has(
              JSON.stringify(queryKey)
            );
            if (
              !wasRemoved ||
              !data ||
              !Array.isArray(data.ids) ||
              data.ids.includes(meetingEntityId)
            ) {
              return;
            }
            queryClient.setQueryData<CrmObjectListSentinelPage>(queryKey, {
              ...data,
              ids: [...data.ids, meetingEntityId],
            });
          });
        logger.debug(
          'Dismiss meeting: restored entity into cache after failed dismiss',
          { meetingEntityId, orgId }
        );
        throw error;
      } finally {
        inFlightRef.current.delete(meetingEntityId);
        forceRender(n => n + 1);
        // Invalidate only the specific old keys we updated — NOT the newly
        // seeded keys. A broad prefix invalidation would hit the seeded
        // newQueryKey entries too, triggering a refetch that races the
        // pre-seed benefit (the anti-flash guarantee this feature provides).
        updatedOldQueryKeys.forEach(oldQueryKey => {
          void queryClient.invalidateQueries({
            queryKey: oldQueryKey,
            exact: true,
          });
        });
      }
    },
    [orgId, queryClient]
  );

  return { dismissMeeting, isDismissing };
}
